// Wave 20.49g — Aero AI nudge card mounted above the Wake.
//
// Reads useWakeNudge and renders: kicker + one-sentence headline +
// weather subline + row of action buttons. Deterministic MVP;
// a later commit can slot in a streamed sentence from aero-ai
// without changing this component's shape (the LLM would only
// replace `headline`; actions stay client-authored).

import React from 'react';
import { Box, Button, IconButton, Stack, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { colors } from '../theme';
import { useWakeNudge } from '../hooks/useWakeNudge';

export default function WakeAi() {
  const nudge = useWakeNudge();

  return (
    <Box
      sx={{
        // Wave 20.49cm — normal flow. Placement moved to the parent
        // (Wake.tsx bottom chrome column) so the card sits in the
        // dead space below the wake and the embed reserves room for
        // it, instead of the card sitting on top of the streams'
        // origin bundle.
        width: '100%',
        maxWidth: 720,
        p: { xs: 2, md: 2.5 },
        borderRadius: 3,
        backgroundColor: 'rgba(10, 14, 26, 0.86)',
        backdropFilter: 'blur(20px)',
        border: `1px solid ${colors.borderFocus}`,
        boxShadow: '0 24px 56px rgba(0,0,0,0.55)',
        pointerEvents: 'auto',
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography
          sx={{
            color: colors.brand,
            fontSize: '0.65rem',
            fontWeight: 800,
            letterSpacing: '0.22em',
          }}
        >
          AERO AI
        </Typography>
        <IconButton
          size="small"
          onClick={nudge.refresh}
          aria-label="Refresh"
          sx={{ color: colors.textMuted, '&:hover': { color: colors.brand } }}
        >
          <RefreshIcon fontSize="small" />
        </IconButton>
      </Stack>
      <Typography
        sx={{
          color: colors.text,
          fontSize: { xs: '1rem', md: '1.15rem' },
          fontWeight: 700,
          lineHeight: 1.35,
          mb: nudge.subline ? 0.5 : 1.5,
          minHeight: 24,
        }}
      >
        {nudge.headline}
      </Typography>
      {nudge.subline && (
        <Typography sx={{ color: colors.textMuted, fontSize: '0.8rem', mb: 1.5 }}>
          {nudge.subline}
        </Typography>
      )}
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {nudge.actions.map((a, i) => (
          <Button
            key={a.key}
            href={a.href}
            variant={i === 0 ? 'contained' : 'outlined'}
            size="small"
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              backgroundColor: i === 0 ? colors.brand : 'transparent',
              color: i === 0 ? '#0A0E1A' : colors.text,
              borderColor: i === 0 ? colors.brand : colors.border,
              '&:hover': {
                backgroundColor: i === 0 ? colors.brand : 'rgba(106,255,69,0.08)',
                borderColor: colors.brand,
              },
            }}
          >
            {a.label}
          </Button>
        ))}
      </Stack>
    </Box>
  );
}
